import type {ClientTool} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {AhpDebugEventInput as DebugEventInput} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'

import type {CreateSessionConfirmation} from './create-session-tool'
import {buildSessionHandoffClientTools} from './session-handoff-tools'
import {buildSessionTitleActiveClient, buildSessionTitleClientTool} from './session-title-tool'

/**
 * Live-relay + cache write the session-title tool needs. Omit the whole
 * `sessionTitle` block for runtimes whose session has no real Mission Control
 * task to rename (e.g. a local fauxpilot session keyed by a random uuid).
 */
export interface SessionTitleConfig {
  /** Dispatch the rename onto the live relay session. */
  dispatchSessionRename: (taskId: string, title: string) => void
  /** Optimistically update local title caches after a successful dispatch. */
  onSessionRenamed?: (taskId: string, title: string) => void
  /** Returns true after a user rename so generated titles cannot overwrite it. */
  isUserRenameLocked?: () => boolean
}

export interface ChatAdapterToolsConfig {
  /** Copilot API base URL used to list/create cloud agent sessions. */
  apiURL: string
  /** Enable the agent-callable `set_session_title` tool and user-initiated renames. */
  sessionTitle?: SessionTitleConfig
  /**
   * Navigate the browser to a cloud agent session by task id. Supplied by the
   * React host (which owns the router); when omitted the view_sessions tool is
   * not contributed.
   */
  navigateToSession?: (taskId: string) => void
  /**
   * Ask the user to approve starting a cloud agent session before create_session
   * writes anything. Supplied by the React host (which owns the confirm UI);
   * when omitted the write-capable create_session tool is not contributed.
   */
  confirmCreateSession?: (details: CreateSessionConfirmation) => Promise<boolean>
}

export interface ChatClientToolsContext {
  session: {taskId: string}
  appendDebugEvent: (event: DebugEventInput) => void
}

export interface ChatAdapterToolsSlice {
  activeClient?: typeof buildSessionTitleActiveClient
  clientTools?: (context: ChatClientToolsContext) => ClientTool[]
}

/**
 * Build Chat's host-bound client tool contributions and the session-title
 * rename machinery that guards them. Chat declares the `host-bound` policy, so
 * the bootstrap boundary pairs these with the host-independent baseline on
 * every establish mode. Other Mission Control paths declare narrower policies
 * and do not advertise the baseline, so do not assume it is always present.
 */
export function createChatAdapterTools(config: ChatAdapterToolsConfig): ChatAdapterToolsSlice {
  const handoffTools = () =>
    buildSessionHandoffClientTools({
      apiURL: config.apiURL,
      navigateToSession: config.navigateToSession,
      confirmCreateSession: config.confirmCreateSession,
    })

  const titleConfig = config.sessionTitle
  if (!titleConfig) {
    return {clientTools: handoffTools}
  }

  const {dispatchSessionRename, onSessionRenamed, isUserRenameLocked} = titleConfig

  function sessionTitleTool({session}: ChatClientToolsContext): ClientTool {
    return buildSessionTitleClientTool({
      renameSession: title => {
        if (isUserRenameLocked?.()) return
        dispatchSessionRename(session.taskId, title)
        onSessionRenamed?.(session.taskId, title)
      },
    })
  }

  return {
    activeClient: buildSessionTitleActiveClient,
    clientTools: context => [sessionTitleTool(context), ...handoffTools()],
  }
}
